'use client';

/**
 * HubSpotMeetingsLoader — client leaf for HubSpot Meetings embed
 * (https://meetings.hubspot.com/<slug>?embed=true).
 *
 * Source HTML pattern (from get-a-security-review/page.html):
 *   <div class="meetings-iframe-container" data-src="https://meetings.hubspot.com/<slug>?embed=true"></div>
 *   <script src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"></script>
 *
 * The MeetingsEmbedCode.js script scans for `.meetings-iframe-container` nodes
 * and replaces them with an iframe. We render the placeholder div with the
 * data-src and inject the script once per page.
 */

import { useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
let scriptPromise: Promise<void> | null = null;

function loadMeetingsScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Meetings embed script failed')), {
        once: true,
      });
      // If already loaded, the load event won't fire again — resolve next tick.
      setTimeout(() => resolve(), 0);
      return;
    }
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Meetings embed script failed'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

interface HubSpotMeetingsLoaderProps {
  meetingSrc: string;
  className?: string;
  title?: string;
}

export function HubSpotMeetingsLoader({
  meetingSrc,
  className,
  title,
}: HubSpotMeetingsLoaderProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMeetingsScript().catch(() => {
      // Non-fatal — leave the placeholder div in place.
    });
  }, []);

  return (
    <div
      ref={ref}
      className={`meetings-iframe-container ${className ?? ''}`.trim()}
      data-src={meetingSrc}
      role="region"
      aria-label={title ?? 'Schedule a meeting'}
    />
  );
}
